import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { motion } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CheckoutPage = () => {
  const { cart, clearCart } = useCart();
  const navigate = useNavigate();
  const [categories, setCategories] = useState({});
  const [formData, setFormData] = useState({
    customer_name: '',
    phone: '',
    whatsapp: '',
    email: '',
    address: '',
    transport_mode: '',
    delivery_address: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [placedOrderPhone, setPlacedOrderPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [whatsappError, setWhatsappError] = useState('');

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/categories`).then(res => {
      const map = {};
      res.data.forEach(c => { map[c.id] = c.name; });
      setCategories(map);
    }).catch(() => {});
    // Auto-fill from saved profile
    const savedProfile = localStorage.getItem('checkout_profile');
    if (savedProfile) {
      try {
        const profile = JSON.parse(savedProfile);
        setFormData(prev => ({ ...prev, ...profile }));
      } catch {}
    }
  }, []);

  const handlePhoneChange = (field, value) => {
    const numericOnly = value.replace(/\D/g, '').slice(0, 10);
    setFormData({ ...formData, [field]: numericOnly });
    const errorSetter = field === 'phone' ? setPhoneError : setWhatsappError;
    if (numericOnly.length > 0 && numericOnly.length < 10) {
      errorSetter('Phone number must be 10 digits');
    } else {
      errorSetter('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.phone.length !== 10) {
      setPhoneError('Phone number must be 10 digits');
      return;
    }
    if (formData.whatsapp && formData.whatsapp.length !== 10) {
      setWhatsappError('WhatsApp number must be 10 digits');
      return;
    }
    setLoading(true);

    try {
      const orderData = {
        ...formData,
        items: cart.map(item => ({
          product_id: item.product.id,
          product_name: item.product.name,
          category_name: categories[item.product.category_id] || '',
          quantity: item.quantity,
          unit: item.unit,
          price: item.product.price
        }))
      };

      await axios.post(`${BACKEND_URL}/api/orders`, orderData);
      
      // Save profile for auto-fill next time
      localStorage.setItem('checkout_profile', JSON.stringify({
        customer_name: formData.customer_name,
        phone: formData.phone,
        whatsapp: formData.whatsapp,
        email: formData.email,
        address: formData.address
      }));
      
      setPlacedOrderPhone(formData.phone);
      setOrderPlaced(true);
      clearCart();
    } catch (error) {
      console.error('Error placing order:', error);
      alert('Failed to place order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (orderPlaced) {
    return (
      <div className="min-h-screen bg-[#F6F5F2] flex items-center justify-center px-4">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', duration: 0.5 }}
          className="text-center"
          data-testid="order-success"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: [0, 1.2, 1] }}
            transition={{ duration: 0.6 }}
          >
            <CheckCircle2 className="h-24 w-24 text-[#2E7D32] mx-auto mb-4" />
          </motion.div>
          <h2 className="text-3xl font-bold text-[#1A3626] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Order Placed Successfully!
          </h2>
          <p className="text-lg text-gray-600 mb-6">Thank you for your order. We will contact you soon.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate(`/my-orders?phone=${placedOrderPhone}`)}
              className="bg-[#C05A3E] text-white hover:bg-[#A64B31] rounded-md px-6 py-3 font-semibold transition-colors"
              data-testid="view-my-order-btn"
            >
              View My Order
            </button>
            <button
              onClick={() => navigate('/')}
              className="bg-[#1A3626] text-white hover:bg-[#2a5440] rounded-md px-6 py-3 font-semibold transition-colors"
              data-testid="back-to-home-btn"
            >
              Back to Home
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  if (cart.length === 0) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="min-h-screen bg-[#F6F5F2]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl sm:text-4xl font-bold text-[#1A3626] mb-8" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="checkout-title">
          Place Your Order
        </h1>

        <div className="bg-white rounded-lg p-6 mb-6 shadow-sm">
          <h2 className="text-xl font-semibold text-[#1A3626] mb-4">Order Summary</h2>
          <div className="space-y-2">
            {cart.map((item, index) => (
              <div key={index} className="flex justify-between text-sm">
                <span>{item.product.name} ({item.unit})</span>
                <span className="font-medium">x {item.quantity}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-gray-200 mt-4 pt-4">
            <div className="flex justify-between font-semibold text-lg">
              <span>Total Items:</span>
              <span>{cart.reduce((sum, item) => sum + item.quantity, 0)}</span>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-lg p-6 shadow-sm space-y-4" data-testid="checkout-form">
          <h2 className="text-xl font-semibold text-[#1A3626] mb-4">Your Details</h2>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
            <input
              type="text"
              required
              value={formData.customer_name}
              onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
              className="w-full bg-white border-2 border-gray-300 focus:border-[#1A3626] focus:ring-0 rounded-md p-3 text-base"
              data-testid="customer-name-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
            <input
              type="tel"
              required
              maxLength={10}
              value={formData.phone}
              onChange={(e) => handlePhoneChange('phone', e.target.value)}
              placeholder="Enter 10-digit phone number"
              className={`w-full bg-white border-2 ${phoneError ? 'border-red-400' : 'border-gray-300'} focus:border-[#1A3626] focus:ring-0 rounded-md p-3 text-base`}
              data-testid="phone-input"
            />
            {phoneError && <p className="text-red-500 text-sm mt-1" data-testid="phone-error">{phoneError}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">WhatsApp Number <span className="text-gray-400 font-normal">(optional)</span></label>
            <input
              type="tel"
            
              maxLength={10}
              value={formData.whatsapp}
              onChange={(e) => handlePhoneChange('whatsapp', e.target.value)}
              placeholder="Enter 10-digit WhatsApp number"
              className={`w-full bg-white border-2 ${whatsappError ? 'border-red-400' : 'border-gray-300'} focus:border-[#1A3626] focus:ring-0 rounded-md p-3 text-base`}
              data-testid="whatsapp-input"
            />
            {whatsappError && <p className="text-red-500 text-sm mt-1" data-testid="whatsapp-error">{whatsappError}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email <span className="text-gray-400 font-normal">(Optional)</span></label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="Enter email (optional)"
              className="w-full bg-white border-2 border-gray-300 focus:border-[#1A3626] focus:ring-0 rounded-md p-3 text-base"
              data-testid="email-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Delivery Address <span className="text-gray-400 font-normal">(Optional)</span></label>
            <textarea
              rows={3}
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              className="w-full bg-white border-2 border-gray-300 focus:border-[#1A3626] focus:ring-0 rounded-md p-3 text-base"
              data-testid="address-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Mode of Transport <span className="text-gray-400 font-normal">(Optional)</span></label>
            <input
              type="text"
              value={formData.transport_mode}
              onChange={(e) => setFormData({ ...formData, transport_mode: e.target.value })}
              placeholder="e.g., Road, Courier, Self Pickup"
              className="w-full bg-white border-2 border-gray-300 focus:border-[#1A3626] focus:ring-0 rounded-md p-3 text-base"
              data-testid="transport-mode-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Delivery Address (if different) <span className="text-gray-400 font-normal">(Optional)</span></label>
            <input
              type="text"
              value={formData.delivery_address}
              onChange={(e) => setFormData({ ...formData, delivery_address: e.target.value })}
              placeholder="Enter separate delivery address if different"
              className="w-full bg-white border-2 border-gray-300 focus:border-[#1A3626] focus:ring-0 rounded-md p-3 text-base"
              data-testid="delivery-address-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Notes <span className="text-gray-400 font-normal">(Optional)</span></label>
            <textarea
              rows={2}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Any special instructions or notes"
              className="w-full bg-white border-2 border-gray-300 focus:border-[#1A3626] focus:ring-0 rounded-md p-3 text-base"
              data-testid="notes-input"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#C05A3E] text-white hover:bg-[#A64B31] rounded-md px-6 py-4 font-semibold text-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="place-order-button"
          >
            {loading ? 'Placing Order...' : 'Place Order'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CheckoutPage;
